import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, ArrowLeft, BookUser, Copy, History, Lock, Plus, Presentation, Redo2, Undo2,
} from "lucide-react";
import { offerApi } from "../../services/offerApi";
import { usePublishNexusPage } from "../../context/nexusPage";
import { computeEstimate, formatEuro, formatNumber } from "../../utils/offerCalc";
import { useVersionedDocument } from "./useVersionedDocument";
import { newLot } from "./treeOps";
import EstimateTable from "./EstimateTable";
import ParamsFields from "./ParamsFields";
import RevisionsPanel from "./RevisionsPanel";
import ClientProfilePanel from "./ClientProfilePanel";
import { SaveStatus, SyncBanners, TitleField, ToolbarButton } from "./EditorChrome";


const ESTIMATE_API = {
  get: offerApi.get,
  save: offerApi.save,
  restore: offerApi.restore,
  revisions: offerApi.revisions,
  label: offerApi.label,
  updateMeta: offerApi.updateMeta,
};

const EstimateEditor = () => {
  const { estimateId } = useParams();
  const id = Number(estimateId);
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [includeOptional, setIncludeOptional] = useState(false);
  const doc = useVersionedDocument({ id, api: ESTIMATE_API, docKey: "estimate", watchRevisions: panel === "revisions" });
  const {
    data, setData, loadError, shown, apply, undo, redo, canUndo, canRedo, setMessage, save,
    canEdit, archived, editable, viewing, viewRevision, restoreRevision, labelRevision, revisions, updateMeta,
    tokenRef, contentRef,
  } = doc;

  // Le total d'une affaire est toujours HT, remisé pour les clients qui ont une remise : c'est le « net ».
  const syncAffaire = async (target) => {
    const current = data?.estimate?.affaire?.total;
    if (current != null && Math.abs(current - target) >= 0.01
      && !window.confirm(`L'affaire affiche ${formatEuro(current)} HT. Le remplacer par ${formatEuro(target)} HT ?`)) return;
    if (!(await save())) return;
    try {
      const result = await offerApi.syncAffaire(id, { include_optional: includeOptional, base_token: tokenRef.current });
      setData((previous) => ({ ...previous, estimate: result.estimate }));
    } catch (error) {
      setMessage(error.message);
    }
  };

  const duplicate = async () => {
    try {
      await save();
      const result = await offerApi.duplicate(id);
      navigate(`/tools/offres/${result.estimate.id}`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  // Ouvre la proposition bâtie sur ce chiffrage, ou la crée.
  const openProposal = async () => {
    try {
      await save();
      const affaireId = data.estimate.affaire_id;
      const existing = (await offerApi.proposals({ affaire_id: affaireId })).items.find((item) => item.estimate_id === id);
      if (!existing && !canEdit) {
        setMessage("Aucune proposition n'est encore bâtie sur ce chiffrage.");
        return;
      }
      const proposalId = existing?.id
        ?? (await offerApi.createProposal({ affaire_id: affaireId, estimate_id: id, title: data.estimate.title })).proposal.id;
      navigate(`/tools/offres/propositions/${proposalId}`);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const setParam = (field, value) =>
    apply((current) => ({ ...current, params: { ...current.params, [field]: value } }), `params.${field}`);

  const addLot = () => {
    const lot = newLot((contentRef.current?.lots?.length || 0) + 1);
    apply((current) => ({ ...current, lots: [...(current.lots || []), lot] }));
    setFocusId(lot.id);
  };

  const totals = useMemo(() => (shown ? computeEstimate(shown) : null), [shown]);

  // Ce que l'utilisateur regarde, pour Nexus.
  usePublishNexusPage(data ? {
    description: `Chiffrage « ${data.estimate.title} »`,
    extra: {
      offer_document: "chiffrage",
      estimate_id: id,
      estimate_title: data.estimate.title,
      affaire_number: data.estimate.affaire?.number,
      consulted_version: viewing ? viewing.revision.number : null,
      can_edit: editable,
    },
  } : null);

  if (loadError) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 py-10 text-center">
        <p className="text-slate-700">{loadError}</p>
        <Link to="/tools/offres" className="text-sm text-blue-700 hover:underline">Retour aux chiffrages</Link>
      </div>
    );
  }
  if (!data || !totals) return <div className="py-10 text-center text-sm text-slate-500">Chargement du chiffrage…</div>;

  const { estimate, client_profile: clientProfile } = data;
  const affaire = estimate.affaire || {};
  const hasOptions = totals.lots.some((lot) => lot.optional);
  const discount = Number(totals.params.client_discount) > 0;
  const syncTarget = (includeOptional && hasOptions ? totals.total : totals.required).net;
  const affaireTotal = affaire.total;
  const affaireInSync = affaireTotal != null && Math.abs(affaireTotal - syncTarget) < 0.01;

  return (
    <div className="mx-auto max-w-[1500px] space-y-4 py-6">
      <div className="space-y-2">
        <Link to="/tools/offres" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Chiffrages
        </Link>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <TitleField value={estimate.title} editable={canEdit} label="Titre du chiffrage" onCommit={(title) => updateMeta({ title })} />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-sm text-slate-500">
              <span className="font-medium text-slate-700">Affaire {affaire.number}</span>
              {affaire.project_id && (
                <Link to={`/project/${affaire.project_id}`} className="hover:text-slate-800 hover:underline">{affaire.project_name}</Link>
              )}
              {affaire.client_name && <span>{affaire.client_name}</span>}
              {!canEdit && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  <Lock className="h-3 w-3" /> Lecture seule
                </span>
              )}
              {archived && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">Archivé</span>}
              <SaveStatus doc={doc} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {canEdit && !archived && (
              <>
                <ToolbarButton onClick={undo} disabled={!canUndo || !editable} title="Annuler (Ctrl+Z)">
                  <Undo2 className="h-4 w-4" /> Annuler
                </ToolbarButton>
                <ToolbarButton onClick={redo} disabled={!canRedo || !editable} title="Rétablir (Ctrl+Y)">
                  <Redo2 className="h-4 w-4" /> Rétablir
                </ToolbarButton>
              </>
            )}
            <ToolbarButton onClick={openProposal} title="Proposition commerciale bâtie sur ce chiffrage">
              <Presentation className="h-4 w-4" /> Proposition
            </ToolbarButton>
            <ToolbarButton onClick={() => setPanel(panel === "revisions" ? null : "revisions")} active={panel === "revisions"} title="Historique des versions">
              <History className="h-4 w-4" /> Versions
            </ToolbarButton>
            {clientProfile && (
              <ToolbarButton onClick={() => setPanel(panel === "client" ? null : "client")} active={panel === "client"} title="Consignes et paramètres du client">
                <BookUser className="h-4 w-4" /> Fiche client
              </ToolbarButton>
            )}
            {canEdit && (
              <>
                <ToolbarButton onClick={duplicate} title="Dupliquer ce chiffrage">
                  <Copy className="h-4 w-4" /> Dupliquer
                </ToolbarButton>
                <ToolbarButton onClick={() => updateMeta({ archived: !archived })} title={archived ? "Désarchiver" : "Archiver"}>
                  {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  {archived ? "Désarchiver" : "Archiver"}
                </ToolbarButton>
              </>
            )}
          </div>
        </div>
      </div>

      <SyncBanners doc={doc} noun="Le chiffrage" />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-4">
          <div className="grid gap-4 xl:grid-cols-[1fr_auto]">
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-slate-700">Paramètres</h2>
              <ParamsFields params={totals.params} disabled={!editable} onChange={setParam} />
            </section>
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:min-w-[18rem]">
              <h2 className="text-sm font-semibold text-slate-700">{hasOptions ? "Total hors options" : "Total"}</h2>
              <div className="mt-1 text-3xl font-semibold tabular-nums text-blue-700">{formatEuro(totals.required.net)}</div>
              <div className="text-sm text-slate-500">
                {discount ? `${formatEuro(totals.required.gross)} HT avant remise · ` : "HT · "}
                {formatNumber(totals.required.days + totals.required.pm_days)} j
              </div>
              {hasOptions && (
                <div className="mt-2 border-t border-slate-100 pt-2 text-sm text-slate-600">
                  Avec options : <span className="font-semibold tabular-nums text-slate-900">{formatEuro(totals.total.net)}</span>
                  {discount && <span className="text-slate-400"> ({formatEuro(totals.total.gross)} avant remise)</span>}
                </div>
              )}
              <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2 text-sm">
                <div className="text-slate-600">
                  Affaire {affaire.number} :{" "}
                  {affaireTotal != null
                    ? <span className="font-medium tabular-nums text-slate-900">{formatEuro(affaireTotal)} HT</span>
                    : <span className="text-slate-400">pas de montant</span>}
                  {affaireTotal != null && !viewing && (
                    <span className={`ml-2 text-xs ${affaireInSync ? "text-emerald-600" : "text-amber-600"}`}>
                      {affaireInSync ? "à jour" : "différent du chiffrage"}
                    </span>
                  )}
                </div>
                {editable && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={() => syncAffaire(syncTarget)} disabled={affaireInSync}
                      className="rounded-full bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-40"
                      title="Reporte le prix HT (remisé), les achats et les heures sur l'affaire">
                      Reporter sur l'affaire
                    </button>
                    {hasOptions && (
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" checked={includeOptional} onChange={(event) => setIncludeOptional(event.target.checked)} />
                        avec les options
                      </label>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>

          <EstimateTable
            content={shown}
            totals={totals}
            readOnly={!editable}
            apply={apply}
            focusId={focusId}
            setFocusId={setFocusId}
          />
          {editable && (
            <button type="button" onClick={addLot}
              className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm text-slate-600 hover:border-slate-400 hover:bg-white">
              <Plus className="h-4 w-4" /> Ajouter un lot
            </button>
          )}
        </div>

        {panel === "revisions" && (
          <RevisionsPanel
            revisions={revisions}
            canEdit={canEdit && !archived}
            viewingId={viewing?.revision?.id}
            onView={viewRevision}
            onRestore={restoreRevision}
            onLabel={labelRevision}
            onClose={() => setPanel(null)}
          />
        )}
        {panel === "client" && clientProfile && (
          <ClientProfilePanel
            profile={clientProfile}
            canEdit={canEdit}
            onSaved={(saved) => setData((previous) => ({ ...previous, client_profile: saved }))}
            onApplyParams={(params) => {
              if (editable) apply((current) => ({ ...current, params: { ...current.params, ...params } }));
            }}
            onClose={() => setPanel(null)}
          />
        )}
      </div>
    </div>
  );
};

export default EstimateEditor;
